import React, { Fragment } from 'react'

import cx from 'classnames'
import { clearFilters, removeFilter } from 'slices/filters'

import FilterTag from 'components/FilterTag'
import { useAppDispatch, useAppSelector } from 'hooks/useRedux'
import {
  FilterConfigAndAvailableValues,
  FilterOperator,
  FilterPage,
  FilterPlurality,
  FilterValues,
  FilterValueType,
  operatorToString
} from 'utils/filters'
import { getFilterConfig } from 'utils/filters'
import { valuesToDateString, valueToDateString } from 'utils/formatDates'

import utilityStyles from '../../styles/utility.module.css'
import styles from './styles.module.css'

const ExtraActiveFiltersTag = ({ count }: { count: number }) => {
  return <span className={styles.extraFiltersTag}>{`+ ${count} more`}</span>
}

const buildFilterChipFromFilter = (
  filter: FilterValues,
  onClickRemove: () => void,
  filters: FilterValues[],
  filterConfiguration: FilterConfigAndAvailableValues[]
) => {
  const config = getFilterConfig(filter, filterConfiguration)

  if (
    !config ||
    filter.values === undefined ||
    filter.values === null ||
    filter.values.length === 0
  ) {
    return
  }

  const totalExtraFilters = Math.max(filter.values.length - 2, 0)

  return (
    <FilterTag
      key={`${filter.filterType}-${filter.filterSubtype}`}
      className={cx(styles.alignmentMargin)}
      onClick={onClickRemove}>
      <div>
        {`${config.filterDisplayName} ${operatorToString(filter.operator)} `}
        {config.plurality === FilterPlurality.MULTIPLE ? (
          filter.values.slice(0, 2).map((item: any, i: number) => (
            <React.Fragment key={i}>
              <span key={i} className={utilityStyles.semiBoldWeight}>
                {item.label}
              </span>
              {//@ts-ignore
              i === 0 && filter.values.length > 1 && ` or `}
            </React.Fragment>
          ))
        ) : config.valueType === FilterValueType.DATE ? (
          filter.operator === FilterOperator.BETWEEN ? (
            filter.values.startDate &&
            filter.values.endDate && (
              <span className={utilityStyles.semiBoldWeight}>
                {valuesToDateString(filter.values)}
              </span>
            )
          ) : (
            <span className={utilityStyles.semiBoldWeight}>{valueToDateString(filter.values)}</span>
          )
        ) : config.valueType === FilterValueType.NUMBER ? (
          <span className={utilityStyles.semiBoldWeight}>{filter.values}</span>
        ) : (
          <span className={utilityStyles.semiBoldWeight}>{filter.values.label}</span>
        )}
        {totalExtraFilters > 0 && (
          <>
            {' or '}
            <span
              className={
                utilityStyles.semiBoldWeight
              }>{`${totalExtraFilters} ${config.extraWord}`}</span>
          </>
        )}
      </div>
    </FilterTag>
  )
}

const ActiveFilters = ({
  filterConfiguration,
  filterPage,
  onFilterChange,
  onClear
}: {
  filterConfiguration: FilterConfigAndAvailableValues[]
  filterPage: FilterPage
  onFilterChange?: () => void
  onClear?: () => void
}) => {
  const { filterGroupsByPage } = useAppSelector(({ filters }) => filters)
  const filterGroups = filterGroupsByPage[filterPage]
  const dispatch = useAppDispatch()

  const onClearFilterGroups = (page: FilterPage) => {
    dispatch(clearFilters({ page }))
  }

  const onRemoveFilter = (page: FilterPage, groupIndex: number, groupNumber: number) =>
    dispatch(removeFilter({ page, groupIndex, groupNumber }))

  const isSomeFilterValid = filterGroups.some(filters =>
    filters.some(filter =>
      getFilterConfig(filter, filterConfiguration)?.getIsFilterValid(filter?.values)
    )
  )

  let totalChips = 0
  let chipsRendered = 0
  const renderAllVisibleChips = (chips: any[], i: number, arr: any[]) => {
    totalChips += chips.length

    const atLastItem = arr.length - 1 === i
    const leftoverChipsExist = totalChips - chipsRendered > 0
    const willOverflow = chipsRendered + chips.length > 3

    if (atLastItem && leftoverChipsExist && chipsRendered !== 0 && willOverflow) {
      return <ExtraActiveFiltersTag count={totalChips - chipsRendered} />
    } else if (chipsRendered >= 3) {
      return <></>
    } else if (chips.length > 3) {
      chipsRendered += 3
      return (
        <Fragment key={i}>
          {i > 0 && <span className={cx(styles.orStyle, utilityStyles.semiBoldWeight)}>or</span>}
          {chips.splice(0, 3)}
          {atLastItem && <ExtraActiveFiltersTag count={totalChips - chipsRendered} />}
        </Fragment>
      )
    } else {
      chipsRendered += chips.length
      return (
        <Fragment key={i}>
          {i > 0 && <span className={cx(styles.orStyle, utilityStyles.semiBoldWeight)}>or</span>}
          {chips}
        </Fragment>
      )
    }
  }

  return (
    <>
      {filterGroups
        .map((filters, groupIndex) =>
          filters.map((filter, filterIndex) => {
            const onClickRemove = () => {
              onFilterChange && onFilterChange()
              onRemoveFilter(filterPage, groupIndex, filterIndex)
            }

            return getFilterConfig(filter, filterConfiguration)?.getIsFilterValid(filter.values)
              ? buildFilterChipFromFilter(filter, onClickRemove, filters, filterConfiguration)
              : null
          })
        )
        .filter(chips => chips?.some(chip => chip !== null))
        .map(renderAllVisibleChips)}
      {isSomeFilterValid && (
        <div
          className={cx(styles.clearText, styles.alignmentMargin)}
          onClick={() => {
            onClear ? onClear() : onClearFilterGroups(filterPage)
          }}>
          Clear all
        </div>
      )}
    </>
  )
}

export default ActiveFilters
